import { Directive, EventEmitter, Input, OnDestroy, OnInit } from "@angular/core";
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormGroup
} from "@angular/forms";
import { first, takeUntil } from "rxjs/operators";

/**
 * Extending this will give you the FormControl logic
 * You still need to add the following to your component providers:
 */
// {
// 	provide: NG_VALUE_ACCESSOR,
// 	useExisting: forwardRef(() => MyFormControlComponent),
// 	multi: true
// }
@Directive()
export abstract class FormArrayComponent<T>
  implements ControlValueAccessor, OnInit, OnDestroy
{
  @Input()
  forceShowErrors = false;

  get formArray(): FormArray {
    return this.formGroup.get("array") as FormArray;
  }

  get value(): Array<Partial<T>> {
    return this.formArray.value;
  }

  // arrays of complex elements must implement this property
  protected abstract defaultElement: T;

  formGroup: FormGroup;

  constructor(protected fb: FormBuilder) {}

  ngOnInit(): void {
    this.initForm();
  }

  protected initForm(): void {
    this.formGroup = this.fb.group({
      array: this.fb.array([])
    });
  }

  protected onDestroyEmitter = new EventEmitter<void>();
  ngOnDestroy(): void {
    this.onDestroyEmitter.emit();
    this.onDestroyEmitter.complete();
  }

  writeValue(val: T[]): void {
    if (val == null) {
      val = [];
    }

    let difference = val.length - this.formArray.length;
    if (difference > 0) {
      while (difference > 0) {
        this.addControl();
        difference--;
      }
    } else if (difference < 0) {
      while (difference < 0) {
        this.removeControl(this.formArray.length - 1);
        difference++;
      }
    }

    this.formGroup.setValue({ array: val });
  }

  registerOnChange(propagateChanges: any): void {
    this.formArray.valueChanges
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe(propagateChanges);
  }

  registerOnTouched(touchCb: any): void {
    this.formGroup.valueChanges
      .pipe(first(), takeUntil(this.onDestroyEmitter))
      .subscribe(touchCb);
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.formGroup.disable();
      return;
    }
    this.formGroup.enable();
  }

  addControl(element?: T): void {
    if (element == null) {
      this.formArray.push(this.defaultMember());
    } else {
      this.formArray.push(this.fb.control(element));
    }
  }

  prependControl(element?: T): void {
    if (element == null) {
      this.writeValue([this.defaultElement].concat(this.value as T[]));
    } else {
      this.writeValue([element].concat(this.value as T[]));
    }
  }

  removeControl(i: number): void {
    this.formArray.removeAt(i);
  }

  private defaultMember(): AbstractControl {
    return this.fb.control(this.defaultElement);
  }
}
